<template>
  <ion-page>
    <ion-header>
      <ion-toolbar>
        <ion-title>Reset Password</ion-title>
      </ion-toolbar>
    </ion-header>
    <ion-content class="ion-padding">
      <div class="reset-password-container">
        <h1>Reset Your Password</h1>
        
        <p v-if="!isProcessing && !isSuccess && !error">
          Please enter a new password below.
        </p>
        
        <div v-if="isProcessing" class="processing-state">
          <ion-spinner></ion-spinner>
          <p>Processing your request...</p>
        </div>
        
        <div v-if="isSuccess" class="success-state">
          <ion-icon :icon="checkmarkCircleOutline" size="large" class="success-icon"></ion-icon>
          <h2>Password Updated Successfully!</h2>
          <p>Your password has been reset and your account is now secure.</p>
          <p class="note">You can now close this window and return to the app.</p>
        </div>
        
        <div v-if="error" class="error-state">
          <ion-icon :icon="alertCircleOutline" size="large" color="danger"></ion-icon>
          <h2>Something Went Wrong</h2>
          <p>{{ error }}</p>
          <div v-if="isPKCEError" class="pkce-error-guidance">
            <p>Your password reset link appears to have expired or is invalid.</p>
            <p>For security reasons, password reset links are only valid for a limited time.</p>
          </div>
          <ion-button expand="block" @click="requestNewPasswordReset" class="mt-2">
            Request New Reset Link
          </ion-button>
          <ion-button expand="block" fill="outline" @click="navigateToLogin" class="mt-2">
            Return to Login
          </ion-button>
        </div>
        
        <form v-if="!isProcessing && !isSuccess && !error" @submit.prevent="updatePassword">
          <div class="password-input-container">
            <label class="password-label">New Password</label>
            <ion-item class="password-input-item">
              <ion-input 
                v-model="password" 
                type="password" 
                required 
                :minlength="8"
                placeholder="Enter your new password"
                class="password-field"
              ></ion-input>
            </ion-item>
            <div class="password-note">Password must be at least 8 characters</div>
          </div>
          
          <div class="password-input-container">
            <label class="password-label">Confirm Password</label>
            <ion-item class="password-input-item">
              <ion-input 
                v-model="confirmPassword" 
                type="password" 
                required
                placeholder="Confirm your new password"
                class="password-field"
              ></ion-input>
            </ion-item>
          </div>
          
          <div class="password-requirements">
            <h3>Password must contain:</h3>
            <ul>
              <li :class="{ valid: isLengthValid }">At least 8 characters</li>
              <li :class="{ valid: hasUppercase }">At least one uppercase letter</li>
              <li :class="{ valid: hasLowercase }">At least one lowercase letter</li>
              <li :class="{ valid: hasNumber }">At least one number</li>
              <li :class="{ valid: passwordsMatch }">Passwords match</li>
            </ul>
          </div>
          
          <div class="button-container">
            <ion-button 
              type="submit" 
              expand="block" 
              :disabled="!isFormValid"
            >
              Reset Password
            </ion-button>
          </div>
        </form>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { supabase, updateUserPassword } from '@/supabase';
import { App } from '@capacitor/app';
import { useUserStore } from '@/store/user';
import { 
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent,
  IonButton, IonItem, IonLabel, IonInput, IonNote, IonSpinner, IonIcon,
  toastController
} from '@ionic/vue';
import { checkmarkCircleOutline, alertCircleOutline } from 'ionicons/icons';

const router = useRouter();
const userStore = useUserStore();
const password = ref('');
const confirmPassword = ref('');
const isProcessing = ref(false);
const isSuccess = ref(false);
const error = ref('');
const isPKCEError = ref(false);

// Add storage for the recovery tokens
const storedCode = ref('');
const storedAccessToken = ref('');
const storedRefreshToken = ref('');

// Password validation
const isLengthValid = computed(() => password.value.length >= 8);
const hasUppercase = computed(() => /[A-Z]/.test(password.value));
const hasLowercase = computed(() => /[a-z]/.test(password.value));
const hasNumber = computed(() => /[0-9]/.test(password.value));
const passwordsMatch = computed(() => 
  password.value !== '' && password.value === confirmPassword.value
);

const isFormValid = computed(() => 
  isLengthValid.value && 
  hasUppercase.value && 
  hasLowercase.value && 
  hasNumber.value && 
  passwordsMatch.value
);

onMounted(async () => {
  console.log('🔐 Reset password page loaded');
  console.log('Full URL:', window.location.href);
  console.log('Path:', window.location.pathname);
  console.log('Hash:', window.location.hash);
  console.log('Search:', window.location.search);
  
  // Store hash immediately before any other operations
  const hash = window.location.hash;
  if (hash && hash.includes('type=recovery')) {
    console.log('✅ Found recovery token in hash, storing immediately');
    try {
      const hashParams = new URLSearchParams(hash.replace('#', ''));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      
      if (!accessToken) {
        console.error('❌ No access token found in hash');
        throw new Error('Invalid recovery link format');
      }
      
      console.log('✅ Found access token in hash');
      storedAccessToken.value = accessToken;
      storedRefreshToken.value = refreshToken || '';
    } catch (err: any) {
      console.error('❌ Error parsing hash:', err);
      error.value = 'The password reset link appears to be invalid. Please request a new reset link.';
    }
  }
  
  try {
    // Get current session and user profile
    const { data: { session } } = await supabase.auth.getSession();
    console.log('Current session:', session ? 'Present' : 'None');
    
    if (session?.user) {
      console.log('User found in session:', session.user.id);
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();
        
      if (profileError) {
        console.error('Error fetching user profile:', profileError);
      } else if (userProfile) {
        console.log('User profile loaded successfully');
        userStore.setUser({
          id: userProfile.id,
          username: userProfile.username,
          isAdmin: userProfile.is_admin,
          isSuperAdmin: userProfile.is_super_admin,
          isOwner: userProfile.is_owner,
          rooftop_id: userProfile.rooftop_id,
          full_name: '',
          avatar_url: '',
          updated_at: ''
        });
      }
    }

    // Set up app state change listener
    App.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
        console.log('App became active, reinitializing user data');
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { data: userProfile, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();
            
          if (!profileError && userProfile) {
            console.log('User profile reinitialized successfully');
            userStore.setUser({
              id: userProfile.id,
              username: userProfile.username,
              isAdmin: userProfile.is_admin,
              isSuperAdmin: userProfile.is_super_admin,
              isOwner: userProfile.is_owner,
              rooftop_id: userProfile.rooftop_id,
              full_name: '',
              avatar_url: '',
              updated_at: ''
            });
          }
        }
      }
    });

    // Check if we have valid tokens
    if (!storedAccessToken.value) {
      console.log('❌ No valid recovery token found - showing error');
      error.value = 'Invalid or expired reset link. Please request a new password reset.';
      isPKCEError.value = true;
    }
  } catch (err) {
    console.error('❌ Error during initialization:', err);
    error.value = 'An error occurred during initialization. Please try again.';
  }
});

const updatePassword = async () => {
  if (!isFormValid.value) return;
  
  try {
    isProcessing.value = true;
    error.value = '';
    
    console.log('Attempting to update password');
    
    // Check if we have stored tokens
    if (!storedCode.value && !storedAccessToken.value) {
      throw new Error('Your password reset session has expired. Please request a new password reset link.');
    }
    
    // Use our custom function that leverages the stored tokens directly
    const { success, error: updateError } = await updatePasswordWithStoredTokens(password.value);
    
    console.log('Password update response:', success);
    
    if (!success) {
      throw new Error(updateError || 'An unknown error occurred');
    }
    
    console.log('Password updated successfully');
    
    // Mark as successful and stop - no navigation needed
    isSuccess.value = true;
    
  } catch (err: any) {
    console.error('Error updating password:', err);
    error.value = err.message || 'An error occurred while resetting your password.';
    
    if (err.message?.includes('session') || err.message?.includes('token') || 
        err.message?.includes('expired') || err.message?.includes('invalid')) {
      isPKCEError.value = true;
    }
  } finally {
    isProcessing.value = false;
  }
};

const resetError = () => {
  error.value = '';
  isPKCEError.value = false;
};

const navigateToLogin = () => {
  // CRITICAL FIX: Use the most reliable navigation method
  console.log('🔄 Navigating to login page with clean state...');
  
  // Force a clean navigation to root
  window.location.href = '/';
};

const requestNewPasswordReset = async () => {
  try {
    // Use window.location instead of router.push to avoid navigation guard issues
    window.location.href = '/?showResetForm=true';
  } catch (err: any) {
    console.error('Error requesting password reset:', err);
    const toast = await toastController.create({
      message: 'Unable to process your request. Please try again.',
      duration: 3000,
      color: 'danger'
    });
    toast.present();
  }
};

// Update the password update function to handle tokens better
const updatePasswordWithStoredTokens = async (password: string) => {
  console.log('🔄 Starting password update with stored tokens');
  console.log('Token status:', {
    hasStoredCode: !!storedCode.value,
    hasStoredAccessToken: !!storedAccessToken.value,
    hasStoredRefreshToken: !!storedRefreshToken.value
  });
  
  try {
    let userId = null;
    let accessToken = null;

    // Try with stored access token first (if available)
    if (storedAccessToken.value) {
      console.log('🔑 Using stored access token for direct password update');
      accessToken = storedAccessToken.value;
      
      // First, get the user ID from the JWT token
      try {
        const tokenData = JSON.parse(atob(accessToken.split('.')[1]));
        userId = tokenData.sub;
        console.log('✅ Got user ID from token:', userId);
      } catch (err) {
        console.error('❌ Error parsing token:', err);
      }
      
      // Make a direct call to the Supabase Auth API
      console.log('🌐 Making direct API call to Supabase Auth');
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/auth/v1/user`, {
        method: 'PUT',
        headers: {
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
      });
      
      if (response.ok) {
        console.log('✅ Auth password update successful with stored access token');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Access token update failed:', errorData);
        throw new Error('Failed to update auth password');
      }
    }
    
    // If we don't have a user ID yet, try with stored code
    if (!userId && storedCode.value) {
      console.log('🔑 Using stored code for session exchange');
      
      // First exchange the code for a session
      console.log('🌐 Exchanging code for session');
      const { data: sessionData, error: sessionError } = await supabase.auth.exchangeCodeForSession(storedCode.value);
      
      if (sessionError) {
        console.error('❌ Failed to exchange code for session:', sessionError);
        throw new Error('Failed to get session from code');
      }
      
      if (sessionData?.session?.access_token) {
        console.log('✅ Got new session from code, attempting password update');
        accessToken = sessionData.session.access_token;
        
        // Get user ID from the new session token
        try {
          const tokenData = JSON.parse(atob(accessToken.split('.')[1]));
          userId = tokenData.sub;
          console.log('✅ Got user ID from session:', userId);
        } catch (err) {
          console.error('❌ Error parsing session token:', err);
        }
        
        // Make a direct call to the Supabase Auth API
        console.log('🌐 Making direct API call to Supabase Auth with session token');
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/auth/v1/user`, {
          method: 'PUT',
          headers: {
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ password })
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('❌ Code-derived token update failed:', errorData);
          throw new Error('Failed to update auth password');
        }
        
        console.log('✅ Auth password update successful with code-derived token');
      }
    }
    
    // If we have a user ID, update the auth password only
    if (userId) {
      console.log('✅ Auth password updated successfully');
      return { success: true, error: null };
    }
    
    // If we get here without a user ID, something went wrong
    console.error('❌ No user ID available for password update');
    return { 
      success: false, 
      error: 'Unable to complete password update. Please try again.'
    };
    
  } catch (err: any) {
    console.error('❌ Error in custom password update function:', err);
    return { 
      success: false, 
      error: err.message || 'An unexpected error occurred during password reset'
    };
  }
};
</script>

<style scoped>
.reset-password-container {
  max-width: 500px;
  margin: 0 auto;
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #2C2C2E;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

/* Add styling for the main title */
.reset-password-container h1 {
  color: #ffffff;
  font-size: 32px;
  margin-bottom: 16px;
  font-weight: 600;
  text-align: center;
}

.reset-password-container p {
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 32px;
  text-align: center;
  font-size: 16px;
  line-height: 1.5;
}

.processing-state,
.success-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 20px;
  width: 100%;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.processing-state ion-spinner,
.success-state ion-icon,
.error-state ion-icon {
  margin-bottom: 24px;
  font-size: 64px;
}

/* Updated password requirements box styling */
.password-requirements {
  margin: 24px 0;
  padding: 24px;
  background-color: rgba(131, 131, 131, 0.1);
  border-radius: 16px;
  border: 1px solid rgba(131, 131, 131, 0.2);
  width: 100%;
}

.password-requirements h3 {
  margin-top: 0;
  font-size: 18px;
  color: #ffffff;
  margin-bottom: 16px;
}

.password-requirements ul {
  list-style-type: none;
  padding-left: 5px;
  margin: 0;
}

.password-requirements li {
  margin-bottom: 14px;
  color: rgba(255, 255, 255, 0.6);
  position: relative;
  padding-left: 28px;
  font-size: 15px;
}

.password-requirements li:before {
  content: "×";
  position: absolute;
  left: 0;
  color: #FF3B30;
  font-size: 18px;
}

.password-requirements li.valid {
  color: #4cd964;
}

.password-requirements li.valid:before {
  content: "✓";
  color: #34C759;
}

.button-container {
  margin-top: 32px;
  width: 100%;
}

/* Updated form styling */
form {
  margin-top: 24px;
  width: 100%;
  padding: 0;
  background-color: transparent;
  border: none;
}

/* Make password inputs much more visible with better label visibility */
.password-input-container {
  margin-bottom: 24px;
  width: 100%;
}

.password-label {
  display: block;
  color: #000000;
  font-weight: 700;
  font-size: 18px;
  margin-bottom: 8px;
  padding-left: 4px;
}

.password-note {
  color: #666666;
  font-size: 13px;
  margin-top: 6px;
  padding-left: 4px;
}

/* Updated item styles for the new approach */
.password-input-item {
  --background: #FFFFFF;
  --border-color: #838383;
  --border-width: 2px;
  --border-radius: 12px;
  --padding-start: 16px;
  --padding-end: 16px;
  --inner-padding-end: 16px;
  --inner-padding-start: 16px;
  --highlight-height: 0;
  margin: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

/* Light mode label color */
@media (prefers-color-scheme: light) {
  .password-label {
    color: #000000;
  }
}

/* Dark mode label color */
@media (prefers-color-scheme: dark) {
  .password-label {
    color: #FFFFFF;
  }
  
  .password-note {
    color: rgba(255, 255, 255, 0.7);
  }
}

/* Change button styling to use gray with white text for both modes */
.button-container ion-button {
  --background: #838383;
  --background-activated: #6e6e6e;
  --background-hover: #6e6e6e;
  --color: #FFFFFF;
  font-weight: 600;
  --border-radius: 12px;
  --padding-top: 16px;
  --padding-bottom: 16px;
  margin-top: 16px;
  text-transform: none;
  font-size: 18px;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  height: 52px;
}

/* Style for the primary action button */
.button-container ion-button,
.error-state ion-button:first-of-type {
  --background: #838383;
  --background-activated: #6e6e6e;
  --background-hover: #6e6e6e;
  --color: #FFFFFF;
}

/* Style for the secondary action button (outlined) */
.error-state ion-button[fill="outline"] {
  --border-color: #FFFFFF;
  --border-width: 2px;
  --color: #FFFFFF;
  --background: transparent;
  --background-activated: rgba(255, 255, 255, 0.1);
  --background-hover: rgba(255, 255, 255, 0.1);
}

/* Remove other light mode overrides */
@media (prefers-color-scheme: light) {
  ion-content {
    --background: #1C1C1E;
  }
  
  .reset-password-container {
    background-color: #2C2C2E;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
    border: none;
  }
  
  h1, h2, h3 {
    color: #FFFFFF;
  }
  
  p {
    color: rgba(255, 255, 255, 0.7);
  }
  
  .password-input-item {
    --background: #FFFFFF;
    --border-color: #838383;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
  
  .password-requirements {
    background-color: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .password-requirements h3 {
    color: #FFFFFF;
  }
  
  .password-requirements ul li {
    color: rgba(255, 255, 255, 0.6);
  }
  
  .password-requirements ul li.valid {
    color: #4CD964;
  }
  
  .password-input-item.item-has-focus {
    --background: #FFFFFF;
    --border-color: #838383;
    box-shadow: 0 0 15px rgba(131, 131, 131, 0.3);
  }
  
  .button-container ion-button,
  .error-state ion-button:first-of-type {
    --background: #838383;
    --background-activated: #6e6e6e;
    --background-hover: #6e6e6e;
    --color: #FFFFFF;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    border: none;
  }
  
  .error-state ion-button[fill="outline"] {
    --border-color: #FFFFFF;
    --color: #FFFFFF;
  }
  
  .password-label {
    color: #FFFFFF;
  }
  
  .password-note {
    color: rgba(255, 255, 255, 0.7);
  }
  
  ion-header ion-toolbar {
    --background: rgba(28, 28, 30, 0.8);
    --color: #ffffff;
  }
  
  ion-header ion-title {
    color: #ffffff;
  }
  
  .error-state h2 {
    color: #ffffff;
  }
  
  .error-state p {
    color: rgba(255, 255, 255, 0.7);
  }
  
  .success-state h2 {
    color: #ffffff;
  }
  
  .success-state p {
    color: rgba(255, 255, 255, 0.8);
  }
  
  .note {
    color: rgba(255, 255, 255, 0.6);
  }
  
  .pkce-error-guidance {
    background-color: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .pkce-error-guidance p {
    color: rgba(255, 255, 255, 0.8);
  }
}

/* Dark mode specific overrides */
@media (prefers-color-scheme: dark) {
  .reset-password-container h1 {
    color: #FFFFFF;
  }
  
  .reset-password-container p {
    color: rgba(255, 255, 255, 0.7);
  }
  
  .password-requirements {
    background-color: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .password-requirements h3 {
    color: #FFFFFF;
  }
  
  .password-requirements ul li {
    color: rgba(255, 255, 255, 0.6);
  }
  
  .password-requirements ul li.valid {
    color: #4CD964;
  }
}

/* Header styling */
ion-header ion-toolbar {
  --background: rgba(28, 28, 30, 0.8);
  --color: #ffffff;
  --border-style: none;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

ion-header ion-title {
  color: #ffffff;
  font-size: 20px;
  font-weight: 600;
}

/* Content styling - simplify background */
ion-content {
  --background: #1C1C1E;
}

/* Improve button styling */
.button-container ion-button {
  --background: #838383;
  --background-activated: #6e6e6e;
  --background-hover: #6e6e6e;
  --color: #FFFFFF;
  font-weight: 600;
  --border-radius: 12px;
  --padding-top: 16px;
  --padding-bottom: 16px;
  margin-top: 16px;
  text-transform: none;
  font-size: 18px;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  height: 52px;
}

/* Add focus effect */
.password-input-item.item-has-focus {
  --background: #FFFFFF;
  --border-color: #838383;
  --border-width: 2px;
  box-shadow: 0 0 20px rgba(131, 131, 131, 0.5);
}

/* Improve password requirements styling */
.password-requirements {
  margin: 24px 0;
  padding: 24px;
  background-color: rgba(131, 131, 131, 0.1);
  border-radius: 16px;
  border: 1px solid rgba(131, 131, 131, 0.2);
  width: 100%;
}

/* Add additional styling for error state to make it more clear */
.error-state {
  padding: 32px 24px;
}

.error-state ion-icon {
  font-size: 72px;
  color: #ff3b30;
}

.error-state h2 {
  margin-top: 0;
  font-size: 28px;
  color: #ffffff;
  margin-bottom: 16px;
}

.error-state p {
  color: rgba(255, 255, 255, 0.7);
  margin: 12px 0;
  line-height: 1.5;
}

.note {
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
  margin: 16px 0 20px;
  font-size: 14px;
}

/* Add additional styling for success state to make it more prominent */
.success-state {
  padding: 48px 24px;
  text-align: center;
}

.success-state .success-icon {
  font-size: 80px;
  color: #34C759;
  margin-bottom: 28px;
}

.success-state h2 {
  font-size: 32px;
  color: #ffffff;
  margin: 0 0 20px 0;
}

.success-state p {
  font-size: 17px;
  color: rgba(255, 255, 255, 0.8);
  margin: 10px 0;
  line-height: 1.5;
}

.success-state .note {
  margin-top: 28px;
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
}

.pkce-error-guidance {
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 20px;
  margin: 24px 0;
  text-align: left;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.pkce-error-guidance p {
  margin: 12px 0;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
}

.mt-2 {
  margin-top: 16px;
}

/* Restore original styles for ion-item and ion-input */
ion-item ion-label {
  --color: rgba(255, 255, 255, 0.9);
  margin-bottom: 8px;
  font-weight: 500;
  font-size: 16px;
}

ion-item ion-input {
  --color: #ffffff;
  --placeholder-color: rgba(255, 255, 255, 0.5);
  --padding-top: 24px;
  --padding-bottom: 14px;
  --placeholder-opacity: 0.7;
  caret-color: white;
  letter-spacing: 0.5px;
}

ion-item.ion-touched.ion-invalid {
  --background: rgba(var(--ion-color-danger-rgb), 0.08);
}

ion-item ion-note {
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
  margin-top: 6px;
  font-style: italic;
}

/* Remove duplicate ion-item styles */
ion-item {
  --padding-start: 0;
  --padding-end: 0;
  --inner-padding-end: 0;
}

/* Add focus state */
ion-item.item-has-focus {
  --background: rgba(255, 255, 255, 0.1);
}

ion-item.item-has-focus ion-input {
  --background: rgba(255, 255, 255, 0.08);
}

/* Make both action buttons consistent */
ion-button {
  --padding-top: 16px;
  --padding-bottom: 16px;
  text-transform: none;
  font-weight: 600;
  font-size: 18px;
  --border-radius: 12px;
  height: 52px;
}

/* Restore some field styles */
.password-field {
  --background: #FFFFFF;
  --color: #000000;
  --placeholder-color: #8E8E93;
  --padding-start: 16px;
  --padding-end: 16px;
  --padding-top: 12px;
  --padding-bottom: 12px;
  font-size: 18px;
  font-weight: 500;
  margin-top: 0;
}

/* Restore note styles */
.password-input-item ion-note {
  color: #666666;
  font-size: 13px;
  margin-top: 6px;
  display: block;
  width: 100%;
}
</style> 